import React from 'react';
import { motion } from 'framer-motion';
import Navigation from './Navigation';
import Footer from './Footer';
import { Shield, FileText, Clock } from 'lucide-react';

const TermsPage = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-emerald-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <p className="text-lg text-white/80">
              Last updated: January 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="prose prose-lg prose-invert max-w-none"
          >
            <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/30 backdrop-blur-lg border border-white/10 rounded-3xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-emerald-400" />
                Agreement to Terms
              </h2>
              <p className="text-white/80 leading-relaxed">
                By using Agami Cabs services, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use our services.
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">1. Service Description</h3>
                <div className="text-white/80 space-y-3">
                  <p>Agami Cabs provides premium electric vehicle transportation services in Delhi NCR, including:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Airport transfer services to/from IGI Airport</li>
                    <li>Hourly and daily vehicle rentals with professional chauffeurs</li>
                    <li>Inter-city transportation within the Delhi NCR region</li>
                    <li>Corporate transportation solutions</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">2. Booking and Reservations</h3>
                <div className="text-white/80 space-y-3">
                  <p><strong>2.1 Booking Process:</strong> Reservations can be made through our website, WhatsApp, or phone. All bookings are subject to vehicle availability.</p>
                  <p><strong>2.2 Confirmation:</strong> Your booking is confirmed only after we send you a confirmation message with trip details.</p>
                  <p><strong>2.3 Changes:</strong> Modifications to confirmed bookings must be made at least 2 hours before the scheduled pickup time.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">3. Pricing and Payment</h3>
                <div className="text-white/80 space-y-3">
                  <p><strong>3.1 Fare Structure:</strong> Pricing is based on distance, time, vehicle type, and service category. All fares include GST.</p>
                  <p><strong>3.2 Payment Methods:</strong> We accept cash, UPI, credit cards, and digital wallets.</p>
                  <p><strong>3.3 Additional Charges:</strong> Waiting time beyond 15 minutes, tolls, and parking fees may apply as additional charges.</p>
                  <p><strong>3.4 Surge Pricing:</strong> During peak hours (7-10 AM and 5-8 PM), surge pricing may apply.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">4. Cancellation Policy</h3>
                <div className="text-white/80 space-y-3">
                  <p><strong>4.1 Customer Cancellation:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Free cancellation up to 1 hour before pickup time</li>
                    <li>50% charge for cancellations within 1 hour of pickup</li>
                    <li>Full charge for no-shows</li>
                  </ul>
                  <p><strong>4.2 Company Cancellation:</strong> In rare cases of vehicle breakdown or emergency, we will arrange alternative transportation or provide full refund.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">5. Customer Responsibilities</h3>
                <div className="text-white/80 space-y-3">
                  <p>Customers are expected to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Provide accurate pickup and destination information</li>
                    <li>Be ready at the pickup location at the scheduled time</li>
                    <li>Treat the vehicle and chauffeur with respect</li>
                    <li>Follow all safety guidelines and traffic rules</li>
                    <li>Not engage in any illegal activities during the trip</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">6. Safety and Security</h3>
                <div className="text-white/80 space-y-3">
                  <p><strong>6.1 Vehicle Safety:</strong> All vehicles undergo regular maintenance and safety inspections.</p>
                  <p><strong>6.2 Chauffeur Verification:</strong> All chauffeurs are background-verified and trained professionals.</p>
                  <p><strong>6.3 Insurance:</strong> All vehicles are comprehensively insured for passenger safety.</p>
                  <p><strong>6.4 Emergency Contact:</strong> 24/7 customer support available for emergencies.</p>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">7. Limitation of Liability</h3>
                <div className="text-white/80 space-y-3">
                  <p>Agami Cabs liability is limited to the fare paid for the service. We are not responsible for:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Delays due to traffic, weather, or circumstances beyond our control</li>
                    <li>Loss or damage to personal belongings</li>
                    <li>Missed flights or appointments due to unforeseen circumstances</li>
                  </ul>
                </div>
              </div>

              <div className="bg-slate-800/30 rounded-2xl p-6 border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4">8. Contact Information</h3>
                <div className="text-white/80 space-y-3">
                  <p>For questions about these Terms of Service, please contact us:</p>
                  <ul className="space-y-2">
                    <li><strong>Email:</strong> hello@agamicabs.com</li>
                    <li><strong>Phone:</strong> +91 99999 99999</li>
                    <li><strong>Address:</strong> Delhi NCR, India</li>
                  </ul>
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <Clock className="w-6 h-6 text-emerald-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Updates to Terms</h3>
                </div>
                <p className="text-white/80">
                  We reserve the right to update these terms at any time. Continued use of our services 
                  after changes constitutes acceptance of the new terms. We will notify customers of 
                  significant changes via email or website notification.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;